import React, { useCallback, useContext, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';

import Row from 'components/Common/Row';
import AccountContext from 'contexts/AccountContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import ToastContext from 'contexts/ToastContext';
import STORE from 'store';

import createExportSavedFilter from '../Report/createExportSavedFilter';
import DateInputContext from 'contexts/DateInputContext';
import ReportContext from 'contexts/ReportContext';

import RunHistory from './RunHistory';
import ScheduleReportModal from './ScheduleReportModal';
import useFormState from './useFormState';
import useReportSchedules from './useReportSchedules';
import formatCadence from './formatCadence';

const ScheduleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border-primary);
`;

const EditScheduleModal = ({
  schedule,
  content,
  timeZone,
  createScheduleSavedFilter,
  close,
}: {
  schedule: ReportSchedules.Schedule;
  content?: ReportSchedules.ScheduleContent;
  timeZone: string;
  // Optional: when not provided the existing savedFilterId is preserved.
  createScheduleSavedFilter?: () => Promise<string>;
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { showToast } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(false);

  const {
    recipientUserIds,
    setRecipientUserIds,
    recipientGroupIds,
    setRecipientGroupIds,
    notes,
    onNotesChanged,
    frequency,
    setFrequency,
    timeOfDay,
    onTimeOfDayChanged,
    dayOfWeek,
    setDayOfWeek,
    dayOfMonth,
    setDayOfMonth,
    draftSchedule,
  } = useFormState({ existingSchedule: schedule, timeZone });

  const onSaveClicked = useCallback(async () => {
    if (!draftSchedule || isLoading) return;
    setIsLoading(true);
    const savedFilterId = createScheduleSavedFilter
      ? await createScheduleSavedFilter()
      : schedule.savedFilterId;
    const updated: ReportSchedules.Schedule = {
      ...draftSchedule,
      contentType: schedule.contentType ?? 'report',
      reportId: schedule.reportId,
      reportName: schedule.reportName,
      savedFilterId,
    };
    await STORE.contentDistributions
      .getReportSchedulesRef({ accountId: selectedAccountId })
      .doc(updated.id)
      .set(updated);
    showToast('Schedule updated');
    setIsLoading(false);
    close();
  }, [
    close,
    createScheduleSavedFilter,
    draftSchedule,
    isLoading,
    schedule.contentType,
    schedule.reportId,
    schedule.reportName,
    schedule.savedFilterId,
    selectedAccountId,
    showToast,
  ]);

  const onDeleteClicked = useCallback(async () => {
    setIsLoading(true);
    await STORE.contentDistributions
      .getReportSchedulesRef({ accountId: selectedAccountId })
      .doc(schedule.id)
      .update({ isActive: false });
    showToast('Schedule deleted');
    setIsLoading(false);
    close();
  }, [close, schedule.id, selectedAccountId, showToast]);

  return (
    <ScheduleReportModal
      existingSchedule={schedule}
      content={content}
      recipientUserIds={recipientUserIds}
      setRecipientUserIds={setRecipientUserIds}
      recipientGroupIds={recipientGroupIds}
      setRecipientGroupIds={setRecipientGroupIds}
      notes={notes}
      onNotesChanged={onNotesChanged}
      frequency={frequency}
      setFrequency={setFrequency}
      timeOfDay={timeOfDay}
      onTimeOfDayChanged={onTimeOfDayChanged}
      dayOfWeek={dayOfWeek}
      setDayOfWeek={setDayOfWeek}
      dayOfMonth={dayOfMonth}
      setDayOfMonth={setDayOfMonth}
      timeZone={timeZone}
      isLoading={isLoading}
      onSaveClicked={onSaveClicked}
      onDeleteClicked={onDeleteClicked}
      runHistory={
        <RunHistory scheduleId={schedule.id} nextRunAt={schedule.nextRunAt} />
      }
      close={close}
    />
  );
};

const CreateScheduleModal = ({
  content,
  timeZone,
  createScheduleSavedFilter,
  close,
}: {
  content: ReportSchedules.ScheduleContent;
  timeZone: string;
  createScheduleSavedFilter: () => Promise<string>;
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { showToast } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(false);

  const {
    recipientUserIds,
    setRecipientUserIds,
    recipientGroupIds,
    setRecipientGroupIds,
    notes,
    onNotesChanged,
    frequency,
    setFrequency,
    timeOfDay,
    onTimeOfDayChanged,
    dayOfWeek,
    setDayOfWeek,
    dayOfMonth,
    setDayOfMonth,
    draftSchedule,
  } = useFormState({ existingSchedule: undefined, timeZone });

  const onSaveClicked = useCallback(async () => {
    if (!draftSchedule || isLoading) return;
    setIsLoading(true);
    const savedFilterId = await createScheduleSavedFilter();
    const newSchedule: ReportSchedules.Schedule = {
      ...draftSchedule,
      contentType: content.type,
      reportId: content.id,
      reportName: content.name,
      savedFilterId,
    };
    await STORE.contentDistributions
      .getReportSchedulesRef({ accountId: selectedAccountId })
      .doc(newSchedule.id)
      .set(newSchedule);
    showToast('Schedule created');
    setIsLoading(false);
    close();
  }, [
    close,
    content.id,
    content.name,
    content.type,
    createScheduleSavedFilter,
    draftSchedule,
    isLoading,
    selectedAccountId,
    showToast,
  ]);

  return (
    <ScheduleReportModal
      existingSchedule={undefined}
      content={content}
      recipientUserIds={recipientUserIds}
      setRecipientUserIds={setRecipientUserIds}
      recipientGroupIds={recipientGroupIds}
      setRecipientGroupIds={setRecipientGroupIds}
      notes={notes}
      onNotesChanged={onNotesChanged}
      frequency={frequency}
      setFrequency={setFrequency}
      timeOfDay={timeOfDay}
      onTimeOfDayChanged={onTimeOfDayChanged}
      dayOfWeek={dayOfWeek}
      setDayOfWeek={setDayOfWeek}
      dayOfMonth={dayOfMonth}
      setDayOfMonth={setDayOfMonth}
      timeZone={timeZone}
      isLoading={isLoading}
      onSaveClicked={onSaveClicked}
      onDeleteClicked={undefined}
      close={close}
    />
  );
};

const ScheduleReportModalContainer = ({
  content,
  createScheduleSavedFilter,
  close,
}: {
  content: ReportSchedules.ScheduleContent;
  createScheduleSavedFilter: () => Promise<string>;
  close: () => void;
}) => {
  const { timezone } = useContext(AccountContext);
  const timeZone = timezone ?? 'UTC';
  const { schedules, isLoading: isLoadingSchedules } = useReportSchedules({
    contentId: content.id,
    contentType: content.type,
  });

  const [editingSchedule, setEditingSchedule] = useState<
    ReportSchedules.Schedule | undefined
  >();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  if (isLoadingSchedules) {
    return null;
  }

  const activeSchedules = schedules.filter((s) => s.isActive);

  return (
    <>
      <Form>
        <FormHeader
          title={
            content.type === 'dashboard'
              ? 'Scheduled Deliveries'
              : 'Scheduled Reports'
          }
          onClose={close}
        />
        <FormContent>
          {activeSchedules.length === 0 && (
            <Typography.Body type="Body 12" color="var(--color-icon-secondary)">
              {content.type === 'dashboard'
                ? 'No scheduled deliveries yet'
                : 'No scheduled reports yet'}
            </Typography.Body>
          )}
          {activeSchedules.map((schedule) => (
            <ScheduleRow key={schedule.id}>
              <Typography.Body type="Body 12">
                {formatCadence(schedule.cadence)}
              </Typography.Body>
              <Button
                size="Small"
                type="Secondary"
                label="Edit"
                onClick={() => setEditingSchedule(schedule)}
              />
            </ScheduleRow>
          ))}
        </FormContent>
        <Row spaceBetween>
          <div />
          <Button
            size="Small"
            type="Primary"
            label="Add Schedule"
            onClick={() => setIsCreateOpen(true)}
          />
        </Row>
      </Form>

      <ModalTransition>
        {isCreateOpen && (
          <Modal onClose={() => setIsCreateOpen(false)}>
            <CreateScheduleModal
              content={content}
              timeZone={timeZone}
              createScheduleSavedFilter={createScheduleSavedFilter}
              close={() => setIsCreateOpen(false)}
            />
          </Modal>
        )}
      </ModalTransition>

      <ModalTransition>
        {editingSchedule && (
          <Modal onClose={() => setEditingSchedule(undefined)}>
            <EditScheduleModal
              schedule={editingSchedule}
              content={content}
              timeZone={timeZone}
              createScheduleSavedFilter={createScheduleSavedFilter}
              close={() => setEditingSchedule(undefined)}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

// Gate wraps the container to build the createScheduleSavedFilter callback
// from the current report filter state (via DateInputContext + ReportContext).
const ScheduleReportModalGate = ({ close }: { close: () => void }) => {
  const { report, currentScope, currentDrillDowns } = useContext(ReportContext);
  const { dateField, relativeDateRange, dateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  const { selectedAccountId } = useContext(AccountPickerContext);

  const createScheduleSavedFilter = useCallback(
    () =>
      createExportSavedFilter({
        source: 'report-schedule',
        dateField,
        currentScope,
        currentDrillDowns,
        relativeDateRange,
        dateRange,
        advancedRelativeDateRange,
        accountId: selectedAccountId,
      }),
    [
      advancedRelativeDateRange,
      currentDrillDowns,
      currentScope,
      dateField,
      dateRange,
      relativeDateRange,
      selectedAccountId,
    ],
  );

  if (!report) return null;

  const content: ReportSchedules.ScheduleContent = {
    id: report.id,
    name: report.name,
    type: 'report',
    v3Access: report.v3Access,
    isV3Published: report.isV3Published,
    createdBy: report.createdBy,
  };

  return (
    <ScheduleReportModalContainer
      content={content}
      createScheduleSavedFilter={createScheduleSavedFilter}
      close={close}
    />
  );
};

export { EditScheduleModal, CreateScheduleModal, ScheduleReportModalContainer };
export default ScheduleReportModalGate;
